import { useRouter } from "next/router";
import { FormEvent, useState } from "react";

import { SocialSignUpType } from "../../../../api/types/social.type";
import { createUser } from "../../../../api/users/createUser";
import { getUser } from "../../../../api/users/getMe";
import { loginUser } from "../../../../api/users/login";
import { translationsContent } from "../../../../localization/translations";
import { useLanguage } from "../../../../store/lang.context";
import { useUser } from "../../../../store/user.context";
import { translate } from "../../../../utils/translate";
import { signUpSchema } from "../../../../utils/validations";
import FacebookButton from "../../../ui/button/FacebookSigninButton";
import GoogleButton from "../../../ui/button/GoogleSigninButton";
import Button from "../../../ui/button/SimpleButton";
import CheckBox from "../../../ui/input/CheckBox";
import RadioButton from "../../../ui/input/RadioButton";
import Input from "../../../ui/input/SimpleInput";
import EmailErrorPopup from "../../errorPopup";
import Club from "./Club";
import classes from "./form.module.scss";
import { SubmitObjectType } from "./types/submit.type";

interface Props {
  setSignUpIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLoginIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSocialSignUp: React.Dispatch<React.SetStateAction<SocialSignUpType>>;
}

const submitObject: SubmitObjectType = {
  first_name: { value: "", isValid: true },
  last_name: { value: "", isValid: true },
  email: { value: "", isValid: true },
  password: { value: "", isValid: true },
  phone_number: { value: "", isValid: true },
  country: { value: "France", isValid: true },
  country_order: { value: "France", isValid: true },
  current_restaurant: { value: 1, isValid: true },
  eatfid: { value: false, isValid: true },
  allow_newsletter: { value: false, isValid: true },
  allow_newsletter_sms: { value: false, isValid: true },
  cgv: { value: false, isValid: false },
};

const SignupForm = ({
  setSignUpIsOpen,
  setLoginIsOpen,
  setSocialSignUp,
}: Props) => {
  const [formData, setFormData] = useState<SubmitObjectType>(submitObject);
  useState(false);
  const [error, setError] = useState(false);
  const [errorLabel, setErrorLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [socialLoginError, setSocialLoginError] = useState(false);

  const { setUser } = useUser();
  const { language } = useLanguage();

  const router = useRouter();

  const handleInputChange = (
    identifier: keyof SubmitObjectType,
    value: string | boolean
  ) => {
    if (
      identifier === "eatfid" ||
      identifier === "allow_newsletter" ||
      identifier === "allow_newsletter_sms" ||
      identifier === "cgv"
    ) {
      setFormData((FormDataPreviousState) => ({
        ...FormDataPreviousState,
        [identifier]: {
          value:
            identifier === "cgv"
              ? value
              : identifier === "allow_newsletter" ||
                identifier === "allow_newsletter_sms"
              ? value === "YES"
                ? true
                : false
              : !formData.eatfid.value,
          isValid: identifier === "cgv" ? value : true,
        },
      }));
    } else {
      setFormData((FormDataPreviousState) => ({
        ...FormDataPreviousState,
        [identifier]: {
          value: value,
          isValid: !signUpSchema.validate({ [identifier]: value }).error,
        },
      }));
    }
  };

  const submitForm = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    const input = {
      first_name: formData.first_name.value,
      last_name: formData.last_name.value,
      email: formData.email.value,
      password: formData.password.value,
      phone_number: formData.phone_number.value,
      country: formData.country.value,
      country_order: formData.country.value,
      current_restaurant: formData.current_restaurant.value,
      eatfid: formData.eatfid.value,
      allow_newsletter: formData.allow_newsletter.value,
      allow_newsletter_sms: formData.allow_newsletter_sms.value,
      cgv: formData.cgv.value,
    };

    const validate = signUpSchema.validate(input);

    if (validate.error || !formData.cgv.isValid) {
      setLoading(false);
      setError(true);
      for (const key in formData) {
        const validationItem = formData[key as keyof SubmitObjectType];
        const tmpValidate = signUpSchema.validate({
          [key]: validationItem.value,
        });
        if (tmpValidate.error)
          setFormData((prevState) => ({
            ...prevState,
            [key]: {
              value: validationItem.value,
              isValid: false,
            },
          }));
      }
      document.getElementById("signUpPage")?.scrollIntoView();
      return;
    }

    setError(false);
    const valid = await createUser(input);
    if (valid?.success) {
      const login = await loginUser({
        username: formData.email.value,
        password: formData.password.value,
      });
      if (login) {
        const { success, data } = await getUser();
        if (success) {
          setUser(data);
          setSignUpIsOpen(false);
        }
      }
    } else {
      setError(true);
      const err = valid.error;
      for (const key in err) {
        const item = formData[key as keyof SubmitObjectType];
        setFormData((prevState) => ({
          ...prevState,
          [key as keyof SubmitObjectType]: {
            value: item.value ? item.value : "",
            isValid: false,
          },
        }));
        if (key === "email") setErrorLabel("Email déjà existant");
      }
    }
    setLoading(false);
  };

  const close = () => {
    setSocialLoginError(false);
  };

  const routeToCGV = () => {
    const lang = localStorage.getItem("language");
    switch (lang) {
      case "fr":
        router.push("/conditions-generales-vente");
        break;
      case "en":
        router.push("/en/terms-and-conditions");
        break;
      case "es":
        router.push("/es/condiciones-generales-venta");
        break;
      case "nl":
        router.push("/nl/algemene-verkoopvoorwaarden");
        break;
    }
  };
  const routeToPersonal = () => {
    const lang = localStorage.getItem("language");
    switch (lang) {
      case "fr":
        router.push("/donnees-personnelles");
        break;
      case "en":
        router.push("/en/personal-information");
        break;
      case "es":
        router.push("/es/datos-personales");
        break;
      case "nl":
        router.push("/nl/persoonsgegevens");
        break;
    }
  };

  return (
    <>
      <form
        className={classes.form}
        onSubmit={(event) => {
          if (!loading) {
            submitForm(event);
          }
        }}
      >
        <div className={classes.inputs}>
          <div className={classes.name}>
            <Input<SubmitObjectType, "first_name">
              isValid={formData.first_name.isValid}
              error={error}
              type="text"
              identifier="first_name"
              placeholder={`${translate(
                translationsContent,
                "firstNamePlaceholder",
                language
              )}`}
              required
              onChange={handleInputChange}
            />
          </div>
          <div className={classes.name}>
            <Input<SubmitObjectType, "last_name">
              isValid={formData.last_name.isValid}
              error={error}
              type="text"
              identifier="last_name"
              placeholder={`${translate(
                translationsContent,
                "lastNamePlaceholder",
                language
              )}`}
              required
              onChange={handleInputChange}
            />
          </div>
        </div>

        <Input<SubmitObjectType, "phone_number">
          isValid={formData.phone_number.isValid}
          error={error}
          type="tel"
          identifier="phone_number"
          placeholder={`${translate(
            translationsContent,
            "telephonePlaceholder",
            language
          )}`}
          required
          onChange={handleInputChange}
        />
        <p style={{ fontSize: "12px", color: "grey" }}>
          {translate(translationsContent, "telephoneFooter", language)}
        </p>

        <Input<SubmitObjectType, "email">
          isValid={formData.email.isValid}
          error={error}
          type="email"
          identifier="email"
          placeholder={`${translate(
            translationsContent,
            "setEmail",
            language
          )}`}
          required
          onChange={handleInputChange}
          autoComplete={false}
        />
        <p style={{ fontSize: "12px", color: "grey" }}>
          {translate(translationsContent, "emailFooter", language)}
        </p>

        <Input<SubmitObjectType, "password">
          isValid={formData.password.isValid}
          error={error}
          type="password"
          identifier="password"
          placeholder={`${translate(
            translationsContent,
            "setPassword",
            language
          )}`}
          required
          onChange={handleInputChange}
          autoComplete={false}
        />

        <div className={classes.dividerContainer}>
          <span className={classes.divider} />
          <p>{translate(translationsContent, "loginHeader4", language)}</p>
          <span className={classes.divider} />
        </div>

        <div className={classes.goBackContainer}>
          <p
            className={classes.goBack}
            onClick={() => {
              setSignUpIsOpen(false);
              setLoginIsOpen(true);
            }}
          >
            {translate(translationsContent, "signupHeader2", language)}
          </p>
        </div>

        <div className={classes.buttonGroup}>
          <GoogleButton
            setSocialSignUp={setSocialSignUp}
            setLoginIsOpen={setLoginIsOpen}
            setSignUpIsOpen={setSignUpIsOpen}
            setError={setSocialLoginError}
          />
          <FacebookButton
            setSocialSignUp={setSocialSignUp}
            setLoginIsOpen={setLoginIsOpen}
            setSignUpIsOpen={setSignUpIsOpen}
            setError={setSocialLoginError}
          />
        </div>

        <div style={{ padding: "1.5rem 0" }}>
          <Club<SubmitObjectType, "eatfid">
            onChange={handleInputChange}
            identifier="eatfid"
          />
        </div>
        <div className={classes.radioButtonsContainer}>
          <p>{translate(translationsContent, "newsletter", language)}</p>
          <div className={classes.radioButtons}>
            <p>{translate(translationsContent, "byEmail", language)}</p>
            <RadioButton<SubmitObjectType, "allow_newsletter">
              label={`${translate(
                translationsContent,
                "newsletterYes",
                language
              )}`}
              value="YES"
              name="allow_newsletter"
              identifier="allow_newsletter"
              checked={true}
              onChange={handleInputChange}
            />
            <RadioButton<SubmitObjectType, "allow_newsletter">
              label={`${translate(
                translationsContent,
                "newsletterNo",
                language
              )}`}
              value="NO"
              name="allow_newsletter"
              identifier="allow_newsletter"
              checked={false}
              onChange={handleInputChange}
            />
          </div>
          <div className={classes.radioButtons}>
            <p>{translate(translationsContent, "bySMS", language)}</p>
            <RadioButton<SubmitObjectType, "allow_newsletter_sms">
              label={`${translate(
                translationsContent,
                "newsletterYes",
                language
              )}`}
              value="YES"
              name="allow_newsletter_sms"
              identifier="allow_newsletter_sms"
              checked={true}
              onChange={handleInputChange}
            />
            <RadioButton<SubmitObjectType, "allow_newsletter_sms">
              label={`${translate(
                translationsContent,
                "newsletterNo",
                language
              )}`}
              value="NO"
              name="allow_newsletter_sms"
              identifier="allow_newsletter_sms"
              checked={false}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className={classes.checkboxContainer}>
          <CheckBox<SubmitObjectType, "cgv">
            label={`${translate(translationsContent, "cgv", language)}`}
            identifier="cgv"
            onChange={handleInputChange}
          />
        </div>
        {error && (
          <div className={classes.notValid}>
            {<p>{errorLabel}</p>}
            {!formData.first_name.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupFirstNameError",
                  language
                )}
              </p>
            )}
            {!formData.last_name.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupLastNameError",
                  language
                )}
              </p>
            )}
            {!formData.phone_number.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupPhonenumberError",
                  language
                )}
              </p>
            )}
            {!formData.country.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupOriginCountryError",
                  language
                )}
              </p>
            )}
            {!formData.country_order.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupOrderCountryError",
                  language
                )}
              </p>
            )}
            {!formData.email.isValid && (
              <p>
                {translate(translationsContent, "signupEmailError", language)}
              </p>
            )}
            {!formData.password.isValid && (
              <p>
                {translate(
                  translationsContent,
                  "signupPasswordError",
                  language
                )}
              </p>
            )}
            {!formData.cgv.isValid && (
              <p>
                {translate(translationsContent, "signupTaCError", language)}
              </p>
            )}
          </div>
        )}
        <p style={{ fontSize: "12px", color: "grey" }}>
          {translate(translationsContent, "signupTaCcontent1", language)}{" "}
          <span
            style={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={routeToCGV}
          >
            {translate(translationsContent, "signupTaCcontent2", language)}
          </span>{" "}
          {translate(translationsContent, "and", language)}{" "}
          <span
            style={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={routeToPersonal}
          >
            {translate(translationsContent, "signupTaCcontent3", language)}
          </span>
          <br />
          <br />
          {translate(translationsContent, "signupTaC", language)}
        </p>
        <div style={{ padding: "1.5rem 0 3.5rem 0" }}>
          <Button
            label={translate(translationsContent, "signupHeader1", language)
              .toString()
              .toUpperCase()}
            loading={loading}
          />
        </div>
      </form>
      {socialLoginError && <EmailErrorPopup onClose={close} />}
    </>
  );
};

export default SignupForm;
